import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "./styles/AssetManagement.module.css";
import { UserData } from "../../../Context/DataContext";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { Beneficiarytrustee } from "../Beneficiarytrustee/Beneficiarytrustee";
import NewBeneficiary from "../Add Beneficiary/new_beneficiary";
import NewTrustee from "../Add Trustee/NewTrustee";

/*
  The "+" that adds a beneficiary or a trusted advisor to an asset, for the
  Asset Management people modal.

  A deliberate copy of Add Asset/AddBeneficiary.jsx rather than a reuse of it.
  That component's dropdown is `position: absolute` with `top: 0; bottom: 0`
  and no anchor of its own — the `position: relative` on its wrapper is
  commented out — so it lands on whichever positioned ancestor happens to be
  above it. Inside the Add / Edit Asset modal that resolves to something that
  works; inside this page's modal the nearest one is the fixed backdrop, so the
  list pinned itself to the top of the viewport.

  Fixing it there would move the dropdown in the asset modals and in the
  beneficiary page too, so the positioning lives here instead: this one anchors
  to its own button and nothing outside this folder is touched.

  The behaviour is otherwise the same — the people not yet on the asset, sorted
  by priority then name, and a row at the end for creating a new one.
*/

const FALLBACK = {
  addNew_beneficiaries: "Add New Beneficiary",
  addNew_trustees: "Add New Advisor",
};

// The cap the beneficiary picker applies before it stops offering "add new".
const MAX_ACCOUNT_BENEFICIARIES = 12;

// Kept in step with .picker_menu, which the placement below has to measure against.
const MENU_WIDTH = 230;

function PeoplePicker({ user_type, alreadySelected, onSelect }) {
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  /*
    Where the list is drawn, in viewport coordinates.

    It has to be `position: fixed` rather than absolute: the modal it lives in
    is `max-height: 80vh; overflow-y: auto`, and an absolutely positioned child
    is confined to that scroll box — which is why a third beneficiary
    disappeared under the modal's edge. Fixed takes it out of the clip
    altogether, at the cost of having to place it by hand.
  */
  const [menuAt, setMenuAt] = useState(null);

  const [content, setContent] = useState(websiteCurrentLang?.content);
  useEffect(() => setContent(websiteCurrentLang?.content), [websiteCurrentLang]);

  /*
    The same two strings the asset modal's picker uses, so the wording and its
    translations stay in one place rather than being restated here.
  */
  const addNewLabel =
    user_type === "beneficiaries"
      ? content?.User?.AddAsset?.AddBeneficiary?.addBeneficiaryText2 ||
        FALLBACK.addNew_beneficiaries
      : content?.DublicatedText?.dublicatedText73 || FALLBACK.addNew_trustees;

  /*
    Measures the space around the button and places the list in it: below when
    there is room, above when there is more of it there, and never taller than
    the gap it has to fill — so it scrolls inside itself instead of running off
    the screen.
  */
  const place = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const GAP = 8;
    const EDGE = 12;
    const TALLEST = 260;
    const SHORTEST = 132;
    const below = window.innerHeight - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    const upwards = below < SHORTEST && above > below;
    setMenuAt({
      left: Math.max(
        EDGE,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - EDGE)
      ),
      top: upwards ? undefined : rect.bottom + GAP,
      bottom: upwards ? window.innerHeight - rect.top + GAP : undefined,
      maxHeight: Math.max(SHORTEST, Math.min(TALLEST, upwards ? above : below)),
    });
  };

  // Closing on an outside click, scoped to this picker.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /*
    Fixed coordinates go stale the moment anything moves, and the modal is
    itself scrollable — hence the capture phase, which sees that scroll as well
    as the page's.
  */
  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  const root = (userData && userData.UserData) || {};
  const all = root[user_type] || [];
  const available = all
    .filter((person) => !alreadySelected.includes(person.user_id))
    .sort((a, b) =>
      a.priority === b.priority
        ? String(a.name || "").localeCompare(String(b.name || ""))
        : a.priority - b.priority
    );

  const canCreate =
    user_type === "trustees" ||
    (root.beneficiaries || []).length < MAX_ACCOUNT_BENEFICIARIES;

  return (
    <span className={styles.picker} ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.picker_add}
        aria-label={addNewLabel}
        aria-expanded={open}
        onClick={() => {
          // Measured before it opens, so it never paints in the wrong place.
          if (!open) place();
          setOpen((prev) => !prev);
        }}
      >
        +
      </button>

      {open && (
        <div
          className={styles.picker_menu}
          style={
            menuAt
              ? {
                  left: `${menuAt.left}px`,
                  top: menuAt.top === undefined ? "auto" : `${menuAt.top}px`,
                  bottom:
                    menuAt.bottom === undefined ? "auto" : `${menuAt.bottom}px`,
                  maxHeight: `${menuAt.maxHeight}px`,
                }
              : undefined
          }
        >
          {available.length === 0 && !canCreate && (
            <div className={styles.picker_empty}>—</div>
          )}
          {available.map((person) => (
            <button
              type="button"
              key={person.user_id}
              className={styles.picker_option}
              onClick={() => {
                onSelect(person);
                setOpen(false);
              }}
            >
              <Beneficiarytrustee
                name={[person.name, person.last_name]
                  .filter(Boolean)
                  .join(" ")}
                priority={person.priority || 0}
                deleatable={false}
                avatar={person.profile_avatar}
                styling={{ width: "28px", height: "28px" }}
                textFontSize="9px"
              />
              <span className={styles.picker_name}>
                {[person.name, person.last_name].filter(Boolean).join(" ")}
              </span>
            </button>
          ))}

          {canCreate && (
            <button
              type="button"
              className={`${styles.picker_option} ${styles.picker_option_add}`}
              onClick={() => {
                setOpenAdd(true);
                setOpen(false);
              }}
            >
              <span className={styles.picker_plus}>+</span>
              <span className={styles.picker_name}>{addNewLabel}</span>
            </button>
          )}
        </div>
      )}

      {/*
        The full create-a-person modals, the same ones the asset modal's picker
        opens.

        Wrapped, because both are `position: fixed` with no z-index of their
        own: without a stacking context here the avatars beside this picker
        paint over them. See .picker_overlay.
      */}
      {openAdd && (
        <span className={styles.picker_overlay}>
          {user_type === "beneficiaries" ? (
            <NewBeneficiary onClose={() => setOpenAdd(false)} />
          ) : (
            <NewTrustee onClose={() => setOpenAdd(false)} />
          )}
        </span>
      )}
    </span>
  );
}

export default PeoplePicker;
